import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nProvider } from "./i18n/I18nProvider";
import { CallbackScreen } from "./features/auth/CallbackScreen";
import { AppRoot } from "./App";
import { authStore } from "./lib/auth";
import { sanitizeReturnPath } from "./lib/auth/returnPath";
import { env } from "./lib/env";
import { pushEvent } from "./lib/analytics";
import "react-loading-skeleton/dist/skeleton.css";
import "./theme/astra.css";
import "./theme/theme.css";
import "./index.css";

document.documentElement.dataset.environment = env.environment;

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("root element not found");
const root = createRoot(rootEl);

const BASE = import.meta.env.BASE_URL;
const CALLBACK_PATH = `${BASE}callback`;
const LOGOUT_CALLBACK_PATH = `${BASE}logout-callback`;

async function bootstrap() {
  const url = new URL(window.location.href);
  const isCallback = url.pathname === CALLBACK_PATH;
  const isLogoutCallback = url.pathname === LOGOUT_CALLBACK_PATH;

  if (isCallback) {
    root.render(
      <StrictMode>
        <I18nProvider>
          <CallbackScreen />
        </I18nProvider>
      </StrictMode>,
    );
  }

  let callbackError: string | null = null;
  try {
    if (isCallback) {
      await authStore.completeLogin(url.searchParams);
    } else if (isLogoutCallback) {
      authStore.completeLogout();
    }
  } catch (error) {
    console.error("oauth callback failed", error);
    callbackError =
      error instanceof Error ? error.message : "Authorisation failed.";
    pushEvent("auth_failed", { auth_error: callbackError });
  }

  // A failed sign-in callback stops here with an explanation + a way back,
  // rather than silently dropping the user on the sign-in screen.
  if (isCallback && callbackError) {
    root.render(
      <StrictMode>
        <I18nProvider>
          <CallbackScreen error={callbackError} />
        </I18nProvider>
      </StrictMode>,
    );
    return;
  }

  await authStore.init();

  if (isCallback) {
    // Land where the user was headed before sign-in (deep link + hash), else BASE.
    const authed = authStore.getSnapshot().status === "authenticated";
    const target =
      (authed &&
        sanitizeReturnPath(
          authStore.consumeReturnTo(),
          BASE,
          window.location.origin,
        )) ||
      BASE;
    window.history.replaceState({}, "", target);
  } else if (isLogoutCallback) {
    window.history.replaceState({}, "", BASE);
  }

  root.render(
    <StrictMode>
      <I18nProvider>
        <AppRoot />
      </I18nProvider>
    </StrictMode>,
  );
}

void bootstrap();
